import * as React from 'react';
import { first, size, trim } from 'es-toolkit/compat';
import { useFormikContext } from 'formik';

import { FormTextField, TextFieldProps } from 'ui';
import { core } from '@tock/coretypes';
import { normalizeSmsPhoneStatusType } from 'utils/smsUtils';
import { useActions } from '@tock/shared/utils/connect';
import { useSelectInputClasses } from 'ui/TextField';

export interface FormPhoneFieldProps extends TextFieldProps {
  className?: string;
  id?: string;
  label?: string;
  name?: string;
  onBlur?(evt: React.FocusEvent<HTMLInputElement>): void;
  onValidatePhone?(status: core.SmsPhone.StatusType | undefined): void;
  placeholder?: string;
}

const FormPhoneField = ({
  className,
  helperText,
  id = 'phone',
  label = 'Phone number',
  name = 'phone',
  onBlur,
  onValidatePhone,
  placeholder = 'Phone number',
  ...rest
}: FormPhoneFieldProps) => {
  const {
    actions: {
      patron: { validatePhoneRequest },
    },
  } = useActions();

  const classes = useSelectInputClasses();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values } = useFormikContext<any>();

  const { phone, phoneCountryCode } = values;

  const [warn, setPhoneWarning] = React.useState<string | undefined>();
  const [valid, setPhoneValid] = React.useState<string | undefined>();
  const isMountedRef = React.useRef(true);

  const validate = React.useCallback(
    async ({ phone, phoneCountryCode }) => {
      if (size(phone) === 0 || size(phoneCountryCode) === 0) {
        return;
      }
      if (isMountedRef.current) {
        setPhoneWarning(undefined);
      }

      const response = await validatePhoneRequest({ phone, phoneCountryCode });
      const result: core.ValidatePhoneResponse = response.payload;
      if (!isMountedRef.current) {
        return;
      }

      onValidatePhone?.(normalizeSmsPhoneStatusType(result.smsPhoneConsentStatus));

      if (!result.isValid) {
        setPhoneWarning('Hmmm, we could not validate this phone number');
        setPhoneValid(undefined);
      } else {
        setPhoneValid(`${first(phoneCountryCode.split(' '))} ${phone} is a valid number`);
        setPhoneWarning(undefined);
      }
    },
    [onValidatePhone, validatePhoneRequest]
  );

  React.useEffect(() => {
    isMountedRef.current = true;
    validate({ phone, phoneCountryCode });
    // this is to separate change from blur.
    // we only want this to happen when the country code changes, which is outside fo this component

    return () => {
      isMountedRef.current = false;
    };
  }, [phone, phoneCountryCode, validate]);

  const handlePhoneBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const newPhone = trim(event.target.value);

    if (onBlur) {
      onBlur(event);
    }

    validate({ phone: newPhone, phoneCountryCode: phoneCountryCode });
  };

  return (
    <FormTextField
      warn={warn ? true : undefined}
      valid={valid ? true : undefined}
      helperText={warn || valid || helperText}
      onBlur={handlePhoneBlur}
      className={classes.root}
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      type="tel"
      {...rest}
    />
  );
};
export default FormPhoneField;
